import { supabase } from './supabase';

export interface MaisonAdjiProduct {
  id: string;
  name: string;
  name_en: string | null;
  slug: string;
  description: string | null;
  description_en: string | null;
  price_xof: number | null;
  price_eur: number | null;
  price_usd: number | null;
  category_id: string | null;
  collection_id: string | null;
  is_active: boolean;
  is_available_for_sale: boolean;
}

export interface ProductShot {
  id: string;
  product_id: string;
  url: string;
  alt_text: string | null;
  alt_text_en: string | null;
  caption: string | null;
  caption_en: string | null;
  position: number;
}

/** Produits par id de catégorie */
export async function getProductsByCategoryId(
  categoryId: string
): Promise<MaisonAdjiProduct[]> {
  const { data, error } = await supabase
    .from('products')
    .select('id, name, name_en, slug, description, description_en, price_xof, price_eur, price_usd, category_id, collection_id, is_active, is_available_for_sale')
    .eq('category_id', categoryId)
    .eq('is_active', true)
    .order('created_at', { ascending: false });
  if (error) return [];
  return (data ?? []) as MaisonAdjiProduct[];
}

/** Tous les produits actifs (pour /collections sans collection) */
export async function getAllProducts(options?: {
  query?: string;
  sortKey?: 'created_at' | 'price';
  reverse?: boolean;
}): Promise<MaisonAdjiProduct[]> {
  let q = supabase
    .from('products')
    .select('id, name, name_en, slug, description, description_en, price_xof, price_eur, price_usd, category_id, collection_id, is_active, is_available_for_sale')
    .eq('is_active', true);

  const sortKey = options?.sortKey ?? 'created_at';
  const ascending = !(options?.reverse ?? false);
  if (sortKey === 'price') {
    q = q.order('price_eur', { ascending }).order('price_xof', { ascending });
  } else {
    q = q.order('created_at', { ascending: false });
  }

  const { data, error } = await q;
  if (error) return [];
  let list = (data ?? []) as MaisonAdjiProduct[];

  if (options?.query?.trim()) {
    const term = options.query.trim().toLowerCase();
    list = list.filter(
      (p) =>
        p.name?.toLowerCase().includes(term) ||
        p.name_en?.toLowerCase().includes(term) ||
        p.slug?.toLowerCase().includes(term)
    );
  }

  if (sortKey === 'price' && list.length > 0) {
    const rev = options?.reverse ?? false;
    list = [...list].sort((a, b) => {
      const pa = a.price_eur ?? a.price_xof ?? 0;
      const pb = b.price_eur ?? b.price_xof ?? 0;
      return rev ? pb - pa : pa - pb;
    });
  }

  return list;
}

/** Produits par slug de catégorie (studio, bespoke) */
export async function getProductsByCategorySlug(
  categorySlug: string
): Promise<MaisonAdjiProduct[]> {
  const { data: cat } = await supabase
    .from('categories')
    .select('id')
    .eq('slug', categorySlug)
    .eq('is_active', true)
    .single();

  if (!cat) return [];

  const { data, error } = await supabase
    .from('products')
    .select('id, name, name_en, slug, description, description_en, price_xof, price_eur, price_usd, category_id, collection_id, is_active, is_available_for_sale')
    .eq('category_id', cat.id)
    .eq('is_active', true)
    .order('created_at', { ascending: false });

  if (error) return [];
  return (data ?? []) as MaisonAdjiProduct[];
}

/** Shots groupés par product_id */
export async function getProductShotsByProductIds(
  productIds: string[]
): Promise<Record<string, ProductShot[]>> {
  if (productIds.length === 0) return {};

  const { data, error } = await supabase
    .from('product_shots')
    .select('*')
    .in('product_id', productIds)
    .order('position', { ascending: true });

  if (error) return {};

  const byProduct: Record<string, ProductShot[]> = {};
  for (const row of data ?? []) {
    const pid = row.product_id;
    if (!byProduct[pid]) byProduct[pid] = [];
    byProduct[pid].push(row as ProductShot);
  }
  return byProduct;
}

/** Un produit par slug (pour la fiche produit) */
export async function getProductBySlug(slug: string) {
  const { data, error } = await supabase
    .from('products')
    .select(`
      id, name, name_en, slug, description, description_en, description_html, description_html_en,
      price_xof, price_eur, price_usd, category_id, collection_id, is_active, is_available_for_sale,
      meta_title, meta_title_en, meta_description, meta_description_en
    `)
    .eq('slug', slug)
    .eq('is_active', true)
    .single();

  if (error || !data) return null;
  return data;
}

/** Images produit (page de vente) pour un produit */
export async function getProductImages(productId: string) {
  const { data } = await supabase
    .from('product_images')
    .select('id, url, alt_text, position, is_featured')
    .eq('product_id', productId)
    .order('position');
  return (data ?? []) as { id: string; url: string; alt_text: string | null; position: number; is_featured: boolean }[];
}

/** Première image par produit (pour grilles / fallback) */
export async function getFirstImageByProductIds(
  productIds: string[]
): Promise<Record<string, string>> {
  if (productIds.length === 0) return {};
  const { data } = await supabase
    .from('product_images')
    .select('product_id, url, position')
    .in('product_id', productIds)
    .order('product_id')
    .order('position');
  const out: Record<string, string> = {};
  const seen = new Set<string>();
  for (const row of data ?? []) {
    if (!seen.has(row.product_id)) {
      seen.add(row.product_id);
      out[row.product_id] = row.url;
    }
  }
  return out;
}

export interface LandingPageImage {
  id: string;
  url: string;
  alt_text: string | null;
  alt_text_en: string | null;
  caption: string | null;
  caption_en: string | null;
  link_url: string | null;
  position: number;
  grid_row: number;
}

/** Images de la landing (pour page d’accueil, login, etc.) */
export async function getLandingPageImages(): Promise<LandingPageImage[]> {
  const { data, error } = await supabase
    .from('landing_page_images')
    .select('id, url, alt_text, alt_text_en, caption, caption_en, link_url, position, grid_row')
    .eq('is_active', true)
    .order('grid_row')
    .order('position');
  if (error) return [];
  return (data ?? []) as LandingPageImage[];
}

/** Langue par défaut du site (table site_language_settings) */
export type DefaultSiteLang = 'fr' | 'en';

export async function getDefaultSiteLanguage(): Promise<DefaultSiteLang> {
  const { data, error } = await supabase
    .from('site_language_settings')
    .select('default_language')
    .limit(1)
    .maybeSingle();
  if (error || !data?.default_language) return 'fr';
  return data.default_language === 'en' ? 'en' : 'fr';
}

/** Devise préférée du site (table settings) */
export type PreferredCurrency = 'XOF' | 'EUR';

export async function getPreferredCurrency(): Promise<PreferredCurrency> {
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', 'preferred_currency')
    .maybeSingle();
  if (error || !data?.value) return 'XOF';
  const v = (data.value as string).toUpperCase();
  return v === 'EUR' ? 'EUR' : 'XOF';
}

/** Variantes pour un produit */
export async function getProductVariants(productId: string) {
  const { data } = await supabase
    .from('product_variants')
    .select('id, title, title_en, price, price_xof, price_eur, price_usd, available_for_sale, selected_options, image_url')
    .eq('product_id', productId)
    .order('created_at');
  return (data ?? []) as any[];
}
